// このストアは各ステップの入力済みデータ（基本情報1〜4）をAPIから取得し、必須項目の埋まり状況を判定してモーダル表示可否を制御します。

import {
  getBasicInfoStep1,
  getBasicInfoStep2,
  getBasicInfoStep3,
  getBasicInfoStep4Completed,
  saveBasicInfoStep1,
  saveBasicInfoStep2,
  saveBasicInfoStep3,
  saveBasicInfoStep4,
} from '@/apis/candidate';
import {
  useDesiredConditionStore,
  type DesiredLocation,
} from '@/stores/resume-candidate/DesiredCondition.store';
import { useCareerStore } from '@/stores/resume-candidate/Career.store';
import type { ApiDesiredLocationPrimary } from '@/types/api/candidate';
import type {
  BasicInfoStep1Request,
  BasicInfoStep1Response,
  BasicInfoStep2Request,
  BasicInfoStep2Response,
  BasicInfoStep3Request,
  BasicInfoStep3Response,
  BasicInfoStep4Request,
  BasicInfoStep4Response,
} from '@/types/api/registrationSteps';
import { defineStore } from 'pinia';

type StepStatus = 'idle' | 'loading' | 'loaded' | 'error';

const hasText = (value?: string | null): boolean =>
  typeof value === 'string' && value.trim().length > 0;
const hasNumber = (value?: number | null): value is number =>
  typeof value === 'number' && !Number.isNaN(value);
const hasZeroOrGreater = (value?: number | null): boolean =>
  typeof value === 'number' && !Number.isNaN(value) && value >= 0;
const isBooleanLike = (value: unknown): boolean =>
  typeof value === 'boolean' || value === 0 || value === 1 || value === '0' || value === '1';
const hasFiniteNumber = (value?: number | null): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const flattenDesiredLocations = (locations: DesiredLocation[]): ApiDesiredLocationPrimary[] => {
  return locations.flatMap(location => {
    if (!location.cities.length) {
      return [
        {
          prefecture_id: location.id,
          prefecture_name: location.name,
          parent_id: location.parentId,
          parent_name: location.parentName,
        },
      ];
    }
    return location.cities.map(city => ({
      prefecture_id: location.id,
      prefecture_name: location.name,
      parent_id: location.parentId,
      parent_name: location.parentName,
      city_id: city.id,
      city_name: city.name,
    }));
  });
};

export const useRegistrationStepsStore = defineStore('registrationSteps', {
  state: () => ({
    fetchStatus: 'idle' as StepStatus,
    step1Data: null as BasicInfoStep1Response['data'] | null,
    step2Data: null as BasicInfoStep2Response['data'] | null,
    step3Data: null as BasicInfoStep3Response['data'] | null,
    step4Data: null as BasicInfoStep4Response['data'] | null,
    step4Completed: false,
    initializePromise: null as Promise<void> | null,
  }),
  getters: {
    isStep1Completed(state): boolean {
      const data = state.step1Data;
      if (!data) return false;
      return (
        hasText(data.gender) &&
        hasText(data.zip_code) &&
        hasNumber(data.prefecture_id ?? null) &&
        hasText(data.address)
      );
    },
    isStep2Completed(state): boolean {
      const data = state.step2Data;
      if (!data) return false;
      const marriedSelected = isBooleanLike(data.is_married);
      return (
        marriedSelected &&
        hasNumber(data.school_category_id ?? null) &&
        hasText(data.school_name) &&
        hasText(data.faculty) &&
        hasZeroOrGreater(data.graduated_year ?? null) &&
        hasZeroOrGreater(data.graduated_month ?? null) &&
        hasNumber(data.graduation_classification_id ?? null) &&
        hasZeroOrGreater(data.income_id ?? null)
      );
    },
    isStep3Completed(state): boolean {
      const data = state.step3Data;
      if (!data) return false;
      const occupations = data.candidate_resume_occupations ?? [];
      const validArrayOccupations =
        occupations.length > 0 &&
        occupations.every(
          occupation =>
            hasNumber(occupation?.occupation_id ?? null) &&
            hasZeroOrGreater(occupation?.years_of_experience ?? null)
        );
      const hasSingleOccupation =
        hasNumber(data.occupation_id ?? null) && hasZeroOrGreater(data.years_of_experience ?? null);
      const hasOccupations = validArrayOccupations || hasSingleOccupation;

      const hasWorkHistoryFromArray = (data.candidate_resume_work_histories?.length ?? 0) > 0;
      const hasWorkHistoryFields =
        hasText(data.corporation_name) &&
        hasZeroOrGreater(data.from_year ?? null) &&
        hasZeroOrGreater(data.from_month ?? null) &&
        hasNumber(data.employment_type_id ?? null);

      return hasOccupations && (hasWorkHistoryFromArray || hasWorkHistoryFields);
    },
    isStep4Completed(state): boolean {
      if (state.step4Completed) return true;
      const data = state.step4Data;
      if (!data) return false;
      const willingnessFilled = hasText(data.willingness);
      const desiredLocationsCount = data.candidate_resume_desired_locations?.length ?? 0;
      return willingnessFilled && desiredLocationsCount > 0;
    },
    shouldDisplayModal(): boolean {
      if (this.fetchStatus !== 'loaded') return false;
      if (this.step4Completed) return false;
      return !(
        this.isStep1Completed &&
        this.isStep2Completed &&
        this.isStep3Completed &&
        this.isStep4Completed
      );
    },
  },
  actions: {
    /**
     * force=true のときは loaded 済みでも再取得する
     */
    async initialize(force = false): Promise<void> {
      if (this.fetchStatus === 'loaded' && !force) return;
      if (this.initializePromise && !force) return this.initializePromise;
      this.fetchStatus = 'loading';
      this.initializePromise = (async () => {
        try {
          const step4Res = await getBasicInfoStep4Completed();
          if (step4Res.status < 200 || step4Res.status >= 300) {
            throw new Error(`Step4 completion fetch failed with status ${step4Res.status}`);
          }

          this.step4Completed = Boolean(step4Res.data?.completed);
          if (this.step4Completed) {
            this.fetchStatus = 'loaded';
            return;
          }

          const [step1Res, step2Res, step3Res] = await Promise.all([
            getBasicInfoStep1(),
            getBasicInfoStep2(),
            getBasicInfoStep3(),
          ]);
          [step1Res, step2Res, step3Res].forEach(res => {
            if (res.status < 200 || res.status >= 300) {
              throw new Error(`Step fetch failed with status ${res.status}`);
            }
          });
          this.step1Data = step1Res.data ?? null;
          this.step2Data = step2Res.data ?? null;
          this.step3Data = step3Res.data ?? null;
          this.fetchStatus = 'loaded';
        } catch (error) {
          this.fetchStatus = 'error';
          throw error;
        } finally {
          this.initializePromise = null;
        }
      })();
      return this.initializePromise;
    },

    async saveStep1(payload: BasicInfoStep1Request): Promise<void> {
      const response = await saveBasicInfoStep1(payload);
      if (response.status !== 200) {
        throw response;
      }
      this.step1Data = {
        ...this.step1Data,
        ...payload,
      };
    },

    async saveStep2(payload: BasicInfoStep2Request): Promise<void> {
      const response = await saveBasicInfoStep2(payload);
      if (response.status !== 200) {
        throw response;
      }
      this.step2Data = {
        ...this.step2Data,
        ...payload,
      };
    },

    async saveStep3(payload: BasicInfoStep3Request): Promise<void> {
      const response = await saveBasicInfoStep3(payload);
      if (response.status !== 200) {
        throw response;
      }
      const careerStore = useCareerStore();
      const occupationsWithNames =
        careerStore.candidate_resume_occupations ??
        this.step3Data?.candidate_resume_occupations ??
        [];
      this.step3Data = {
        ...this.step3Data,
        ...payload,
        candidate_resume_occupations: payload.candidate_resume_occupations.map(occupation => {
          const matched = occupationsWithNames.find(
            occ => occ?.occupation_id === occupation.occupation_id
          );
          return {
            occupation_id: occupation.occupation_id,
            years_of_experience: occupation.years_of_experience,
            name: matched?.name ?? '',
            parent_name: matched?.parent_name ?? '',
          };
        }),
        candidate_resume_work_histories: payload.candidate_resume_work_histories ?? [],
        work_experience_description: payload.work_experience_description,
      };
    },

    async saveStep4(payload: BasicInfoStep4Request): Promise<void> {
      const response = await saveBasicInfoStep4(payload);
      if (response.status !== 200) {
        throw response;
      }
      const desiredConditionStore = useDesiredConditionStore();
      const desiredLocations = flattenDesiredLocations(desiredConditionStore.desired_locations);
      this.step4Data = {
        willingness: payload.willingness,
        candidate_resume_desired_locations: desiredLocations,
      };
      this.step4Completed = true;
    },
  },
});
